// Pure simulation state. Rules for this module (borrowed from the spy project,
// kept for a future multiplayer option): everything JSON-serializable, no DOM,
// no Date.now, no Math.random, no classes in state. The sim advances only via
// step(state, command) at a fixed tick rate.

import type { AttackKind } from './attacks';
import type { ProfileId } from './profiles';

export type CharacterId = string;
export type CharacterKind = 'agent' | 'king' | 'training' | 'soldier';
export type Team = 'friendly' | 'enemy';
export type MatchResult = 'playing' | 'victory' | 'defeat' | 'draw';
export type Brain = 'player' | 'training' | 'soldier' | 'guard' | 'assassin' | 'royal';
export type Archetype = 'infantry' | 'archer' | 'knight';
/** Field buildings (outside the walls, raidable) and bailey buildings
 * (inside the courtyard, enterable, gate whole tech branches). */
export type BuildingKind =
  | 'farm'
  | 'market'
  | 'smithy'
  | 'range'
  | 'chapel'
  | 'treasury'
  | 'housing';
export type PurchaseKind = 'infantry' | 'archers' | 'knights' | BuildingKind;

export interface BuildingState {
  id: string;
  team: Team;
  kind: BuildingKind;
  plotIndex: number;
  /** Which plot list plotIndex refers to. */
  plot: 'field' | 'bailey';
  /** Construction 0..1; a building only works (and blocks) when complete. */
  progress: number;
  hp: number;
  maxHp: number;
}

export interface ProjectileState {
  x: number;
  z: number;
  /** Height above sim ground (arrows fly ballistic arcs). */
  y: number;
  vx: number;
  vz: number;
  vy: number;
  team: Team;
  damage: number;
  /** Seconds until the arrow despawns. */
  ttl: number;
  /** Seconds during which wall collision is ignored (clearing own parapet). */
  grace: number;
}
/** Player-level intent for the whole army; the commander AI turns this into
 * per-squad objectives. */
export type Posture = 'defend' | 'attack' | 'raid' | 'rally';

/** What the commander has this squad doing right now. */
export type SquadObjective = 'hold' | 'garrison' | 'assault' | 'intercept' | 'raid' | 'rally';

export interface SquadState {
  id: string;
  team: Team;
  objective: SquadObjective;
  /** Objective anchor target (hold point, raid point, rally point). */
  targetX: number;
  targetZ: number;
  /** Enemy squad this squad is intercepting (its live centroid is chased). */
  targetSquad: string | null;
  anchorX: number;
  anchorZ: number;
  /** Lane the squad assaults/rallies along. */
  laneZ: number;
  facing: number;
  /** Progress along the assault waypoint chain (field -> gate -> keep). */
  advanceStep: number;
}

/** One-tick gameplay event, consumed by the shell for audio/FX. */
export interface SimEvent {
  type:
    | 'swing'
    | 'swing-heavy'
    | 'kick'
    | 'hit'
    | 'blocked'
    | 'guard-break'
    | 'parry'
    | 'dodge'
    | 'death'
    | 'respawn'
    | 'arrow'
    | 'build'
    | 'research'
    | 'king-threat'
    | 'alarm'
    | 'horn'
    | 'raze';
  x: number;
  z: number;
  /** Team context where it matters (deaths for morale, horns for feedback). */
  team?: Team;
}
export type HitOutcome =
  | 'unblocked'
  | 'blocked'
  | 'guardBroken'
  | 'guardCrushed'
  | 'parried'
  | 'dodged';

export interface CharacterState {
  id: CharacterId;
  kind: CharacterKind;
  team: Team;
  brain: Brain;
  profile: ProfileId;
  /** Planar position — most of the battlefield plays out on the XZ plane. */
  x: number;
  z: number;
  /** Standing elevation: 0 on the ground, wall height on a wall-walk, etc.
   * Set by the level system (world.ts), never freely. */
  elev: number;
  /** Last position that had valid footing (revert target after collisions). */
  safeX: number;
  safeZ: number;
  /** Facing in radians; forward vector is (sin heading, cos heading) on XZ. */
  heading: number;
  /** Spawn point, used for training-dummy respawns. */
  spawnX: number;
  spawnZ: number;
  spawnElev: number;
  health: number;
  maxHealth: number;
  baseSpeed: number;
  alive: boolean;
  moving: boolean;
  sprinting: boolean;

  // combat
  stamina: number;
  exhausted: boolean;
  /** Seconds until stamina regeneration resumes. */
  regenDelay: number;
  attacking: boolean;
  attackKind: AttackKind | null;
  attackElapsed: number;
  attackResolved: boolean;
  blocking: boolean;
  /** Ticks the current block has been held — parries require a fresh block. */
  blockHeldTicks: number;
  /** Seconds of stagger remaining; staggered characters cannot move or act. */
  stagger: number;
  dodging: boolean;
  dodgeElapsed: number;
  dodgeDirX: number;
  dodgeDirZ: number;
  /** Tick of the last hit taken — drives hit-flash feedback in the renderer. */
  lastHitTick: number;
  /** Outcome of the last hit event on this character, for feedback. */
  lastHitOutcome: HitOutcome | null;
  /** Tick at which a dead training dummy respawns (null = no respawn). */
  respawnAtTick: number | null;

  // squad membership (soldiers only)
  squadId: string | null;
  squadIndex: number;
  /** Battlefield role for soldiers (melee line, bow, heavy). */
  archetype: Archetype;
  /** Garrison job for guards (patrol, sentry, off-duty, ...). */
  role: string | null;
  /** Patrol route id for patrolling guards. */
  patrolRoute: string | null;
  /** Battlefield nerve (0..1). Low morale routs a soldier toward home. */
  morale: number;
  routing: boolean;

  // per-character AI memory
  aiCooldown: number;
  aiBlockTicks: number;
  aiTargetId: CharacterId | null;
  aiWaypoint: number;
  /** Royal retreat: index into royalChambers, and the route being walked. */
  aiChamber: number;
}

export interface GameState {
  tick: number;
  rngState: number;
  characters: Record<CharacterId, CharacterState>;
  squads: Record<string, SquadState>;
  controlled: CharacterId;
  result: MatchResult;
  gold: Record<Team, number>;
  /** Gold per second, recomputed from base + techs + buildings. */
  goldIncome: Record<Team, number>;
  /** Owned tech ids per team (linear within each branch). */
  research: Record<Team, string[]>;
  /** Tick of the last "king in danger" alert per team, for rate limiting. */
  lastKingAlertTick: Record<Team, number>;
  /** Castle alarm: garrison stays roused until this tick (per castle). */
  alarmUntil: Record<Team, number>;
  /** Army-wide intent per team; the commander translates it into objectives. */
  posture: Record<Team, Posture>;
  buildings: BuildingState[];
  projectiles: ProjectileState[];
  /** Counter for ids of runtime-spawned characters/buildings. */
  nextId: number;
  /** Cleared and refilled every tick. */
  events: SimEvent[];
}

/** Player intent for one tick. Move vector is world-space (the shell resolves
 * camera-relative input before the sim ever sees it) and normalized. */
export interface Command {
  moveX: number;
  moveZ: number;
  sprint: boolean;
  /** Pressed this tick. */
  attackLight: boolean;
  attackHeavy: boolean;
  kick: boolean;
  dodge: boolean;
  /** Block held. Also feints an in-flight heavy when pressed during its windup. */
  block: boolean;
  /** Desired facing (camera yaw for the player); applied when attacking/blocking. */
  faceYaw: number | null;
  /** Set the army's posture; the commander AI works out the details. */
  armyPosture: Posture | null;
  /** Purchase a troop wave or building (F4-F8). */
  buy: PurchaseKind | null;
  /** Research a tech by id (tech tree UI). */
  research: string | null;
  /** Archer intent: loose an arrow along faceYaw/heading. */
  shoot: boolean;
  /** Vertical launch speed for the shot (AI solves the ballistic arc for its
   * target; null = a flat default for player shots). */
  shootVy: number | null;
  switchTo: CharacterId | null;
  /** Debug hooks — no key bindings; used by tests only. */
  debug: 'damageControlled' | 'forceDefeat' | 'forceVictory' | null;
}

export const EMPTY_COMMAND: Command = {
  moveX: 0,
  moveZ: 0,
  sprint: false,
  attackLight: false,
  attackHeavy: false,
  kick: false,
  dodge: false,
  block: false,
  faceYaw: null,
  armyPosture: null,
  buy: null,
  research: null,
  shoot: false,
  shootVy: null,
  switchTo: null,
  debug: null,
};
